/**
 * MonthlyEnergyCalendar Component
 *
 * Calendar heatmap showing daily fixed-vs-Agile cost comparison.
 * Green days = Agile was cheaper, red days = Agile was dearer.
 * Intensity scales with the magnitude of the delta.
 */

import { useMemo, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { useMonthlyDailyComparison } from '@/hooks/useMonthlyDailyComparison'

const FIXED_COLOR = '#f59e0b'
const AGILE_COLOR = '#0ea5e9'

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Green/red intensity buckets (Tailwind-ish colours)
const GREEN_BUCKETS = [
  'rgba(34, 197, 94, 0.12)',  // 1 — lightest
  'rgba(34, 197, 94, 0.25)',  // 2
  'rgba(34, 197, 94, 0.38)',  // 3
  'rgba(34, 197, 94, 0.52)',  // 4
  'rgba(34, 197, 94, 0.68)',  // 5 — strongest
]
const RED_BUCKETS = [
  'rgba(239, 68, 68, 0.12)',
  'rgba(239, 68, 68, 0.25)',
  'rgba(239, 68, 68, 0.38)',
  'rgba(239, 68, 68, 0.52)',
  'rgba(239, 68, 68, 0.68)',
]

function getBucket(delta, maxAbsDelta) {
  if (maxAbsDelta === 0) return 0
  const ratio = Math.abs(delta) / maxAbsDelta
  return Math.min(Math.ceil(ratio * 5), 5) - 1
}

function DayCell({ day, dayData, maxAbsDelta, isToday, onClick }) {
  const [showTooltip, setShowTooltip] = useState(false)

  let bgColor = undefined
  let className = 'relative flex items-center justify-center rounded-md text-xs h-9 w-full cursor-pointer transition-shadow hover:shadow-md'

  if (!dayData) {
    className += ' bg-neutral-50 text-neutral-400'
  } else {
    const bucket = getBucket(dayData.delta_pence, maxAbsDelta)
    bgColor = dayData.delta_pence >= 0 ? GREEN_BUCKETS[bucket] : RED_BUCKETS[bucket]
    className += ' text-neutral-700 font-medium'
  }

  if (isToday) {
    className += ' ring-2 ring-neutral-400'
  }

  return (
    <div
      className={className}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {day}
      {showTooltip && dayData && (
        <div className="absolute z-10 bottom-full mb-1 left-1/2 -translate-x-1/2 bg-white border border-neutral-200 rounded-lg shadow-lg p-2.5 min-w-44 pointer-events-none">
          <p className="font-medium text-neutral-900 text-xs mb-1.5">
            {new Date(dayData.date).toLocaleDateString('en-GB', {
              weekday: 'short', day: 'numeric', month: 'short'
            })}
          </p>
          <div className="space-y-0.5 text-xs">
            <div className="flex justify-between gap-3">
              <span className="text-neutral-500">Usage</span>
              <span className="font-medium">{parseFloat(dayData.kwh_consumed).toFixed(1)} kWh</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-neutral-500">Fixed</span>
              <span className="font-medium" style={{ color: FIXED_COLOR }}>
                £{(parseFloat(dayData.fixed_cost_pence) / 100).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-neutral-500">Agile</span>
              <span className="font-medium" style={{ color: AGILE_COLOR }}>
                £{(parseFloat(dayData.agile_cost_pence) / 100).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between gap-3 pt-1 border-t border-neutral-100">
              <span className="text-neutral-500">Saving</span>
              <span className={`font-medium ${parseFloat(dayData.delta_pence) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {parseFloat(dayData.delta_pence) >= 0
                  ? `£${(parseFloat(dayData.delta_pence) / 100).toFixed(2)}`
                  : `-£${(Math.abs(parseFloat(dayData.delta_pence)) / 100).toFixed(2)}`
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function MonthlyEnergyCalendar({ month, onDayClick, refreshKey }) {
  const monthStr = useMemo(() => {
    const y = month.getFullYear()
    const m = String(month.getMonth() + 1).padStart(2, '0')
    return `${y}-${m}-01`
  }, [month])

  const { data, isLoading } = useMonthlyDailyComparison(monthStr, refreshKey)

  // Build lookup: date string → row
  const dayMap = useMemo(() => {
    const map = {}
    for (const row of data) {
      map[row.date] = row
    }
    return map
  }, [data])

  // Monthly summary
  const summary = useMemo(() => {
    if (!data.length) return null
    const totalKwh = data.reduce((s, r) => s + parseFloat(r.kwh_consumed), 0)
    const totalFixed = data.reduce((s, r) => s + parseFloat(r.fixed_cost_pence), 0)
    const totalAgile = data.reduce((s, r) => s + parseFloat(r.agile_cost_pence), 0)
    return {
      totalKwh,
      fixedCost: totalFixed / 100,
      agileCost: totalAgile / 100,
    }
  }, [data])

  // Max absolute delta for bucket scaling
  const maxAbsDelta = useMemo(() => {
    if (!data.length) return 0
    return Math.max(...data.map(r => Math.abs(parseFloat(r.delta_pence))))
  }, [data])

  // Calendar grid cells
  const calendarCells = useMemo(() => {
    const year = month.getFullYear()
    const m = month.getMonth()
    const daysInMonth = new Date(year, m + 1, 0).getDate()

    // Day of week for 1st of month (0=Sun in JS, convert to Mon=0)
    const firstDayJS = new Date(year, m, 1).getDay()
    const offset = firstDayJS === 0 ? 6 : firstDayJS - 1

    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    const cells = []

    // Empty cells before day 1
    for (let i = 0; i < offset; i++) {
      cells.push({ key: `empty-${i}`, empty: true })
    }

    // Day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({
        key: dateStr,
        day: d,
        dateStr,
        data: dayMap[dateStr] || null,
        isToday: dateStr === todayStr,
      })
    }

    return cells
  }, [month, dayMap])

  if (isLoading) {
    return <Skeleton className="h-[300px] w-full" />
  }

  return (
    <>
      {/* Summary stats */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-sm">
        {summary ? (
          <>
            <div>
              <span className="text-muted-foreground">Usage:</span>{' '}
              <span className="font-semibold">{summary.totalKwh.toFixed(1)} kWh</span>
            </div>
            <div title={`£${summary.fixedCost.toFixed(2)} excl VAT / £${(summary.fixedCost * 1.2).toFixed(2)} inc VAT`}>
              <span className="text-muted-foreground">Fixed:</span>{' '}
              <span className="font-semibold" style={{ color: FIXED_COLOR }}>
                £{summary.fixedCost.toFixed(2)}
              </span>
            </div>
            <div title={`£${summary.agileCost.toFixed(2)} excl VAT / £${(summary.agileCost * 1.2).toFixed(2)} inc VAT`}>
              <span className="text-muted-foreground">Agile:</span>{' '}
              <span className="font-semibold" style={{ color: AGILE_COLOR }}>
                £{summary.agileCost.toFixed(2)}
              </span>
            </div>
            <span className="text-border">|</span>
            <div title={`£${Math.abs(summary.fixedCost - summary.agileCost).toFixed(2)} excl VAT / £${(Math.abs(summary.fixedCost - summary.agileCost) * 1.2).toFixed(2)} inc VAT`}>
              <span className="text-muted-foreground">Saving:</span>{' '}
              <span className={`font-semibold ${summary.agileCost <= summary.fixedCost ? 'text-green-600' : 'text-red-600'}`}>
                {summary.agileCost <= summary.fixedCost
                  ? `£${(summary.fixedCost - summary.agileCost).toFixed(2)}`
                  : `-£${(summary.agileCost - summary.fixedCost).toFixed(2)}`
                }
              </span>
            </div>
          </>
        ) : (
          <span className="text-muted-foreground">No consumption data for this month</span>
        )}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day headers */}
        {DAY_HEADERS.map(d => (
          <div key={d} className="text-center text-xs text-neutral-400 font-medium pb-1">
            {d}
          </div>
        ))}

        {/* Day cells */}
        {calendarCells.map(cell =>
          cell.empty ? (
            <div key={cell.key} />
          ) : (
            <DayCell
              key={cell.key}
              day={cell.day}
              dayData={cell.data}
              maxAbsDelta={maxAbsDelta}
              isToday={cell.isToday}
              onClick={() => onDayClick?.(cell.dateStr)}
            />
          )
        )}
      </div>
    </>
  )
}
